// @ts-nocheck

import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import * as S from './styles';

// Components
import Checkbox from 'app/shared/core/Checkbox';
import Linker from 'app/shared/modules/Linker';
import ListingWrapper from 'app/shared/modules/listing-cards/ListingWrapper';
import { SkeletonCard } from 'app/shared/modules/listing-cards/ListingCardV3';
import ProblemCircleFillRedIcon from 'images/icons/problem-circle-fill-red.svg';

// Analytics
import { useInView } from 'react-intersection-observer';

const HubCollection = ({
  title,
  ariaLabelVerb,
  listings,
  displayCount,
  showOffMarketToggle,
  handleOffMarketToggle = () => {},
  ctaMessage,
  ctaLink,
  noListingsMessage,
  isLoading,
  error,
  analyticsLabel,
  triggerObject,
}) => {
  const [showOffMarketProperties, setShowOffMarketProperties] = useState(false);
  const dispatch = useDispatch();

  const onToggleChange = useCallback(() => {
    handleOffMarketToggle(!showOffMarketProperties);
    setShowOffMarketProperties(!showOffMarketProperties);
  }, [showOffMarketProperties, handleOffMarketToggle, dispatch, analyticsLabel]);

  const renderSkeletons = useCallback(() => {
    const skeletons = [];

    for (let i = 0; i < displayCount; i++) {
      skeletons.push(<SkeletonCard key={i} />);
    }

    return skeletons;
  }, [displayCount]);

  const getCollectionAriaLabel = useCallback(() => {
    if (listings.length === 0) {
      return `This collection contains listings you've ${ariaLabelVerb}. You haven't ${ariaLabelVerb} any ${
        showOffMarketToggle ? (showOffMarketProperties ? 'on nor off-market ' : 'on-market ') : ''
      }properties, when you do, they'll appear here`;
    }

    let ariaLabel = `This collection contains listings you've ${ariaLabelVerb}, `;

    if (showOffMarketToggle) {
      ariaLabel += `${showOffMarketProperties ? '' : 'not '}including off-market properties, `;
    }

    ariaLabel += `you can view all your ${ariaLabelVerb} rentals by following the link at the bottom`;

    return ariaLabel;
  }, [ariaLabelVerb, listings.length, showOffMarketProperties, showOffMarketToggle]);

  const [trackingRef] = useInView({
    triggerOnce: true,
  });

  return (
    <S.Container aria-label={getCollectionAriaLabel()}>
      <S.HeaderContainer>
        <S.HeaderText>{title}</S.HeaderText>
        {showOffMarketToggle && !error && (
          <Checkbox
            label={'Show off-market properties'}
            id={`${title} show off-market properties toggle`}
            onChange={onToggleChange}
            checked={showOffMarketProperties}
          />
        )}
      </S.HeaderContainer>
      {isLoading && (
        <>
          <S.ListingsContainer>{renderSkeletons()}</S.ListingsContainer>
        </>
      )}
      {!isLoading && !error && listings.length !== 0 && (
        <>
          <S.ListingsContainer>
            {listings.slice(0, displayCount).map((listing) => {
              return (
                <ListingWrapper
                  listing={listing}
                  key={listing.aliasEncoded}
                  triggerLocation="my_hub_page"
                  triggerObject={triggerObject}
                />
              );
            })}
          </S.ListingsContainer>

          <S.ctaText>
            <Linker to={ctaLink}>{ctaMessage}</Linker>
          </S.ctaText>
        </>
      )}

      {!isLoading && !error && listings.length === 0 && <S.NoListingMessage>{noListingsMessage}</S.NoListingMessage>}

      {!isLoading && error && (
        <S.ErrorContainer>
          <S.ErrorIcon src={ProblemCircleFillRedIcon} alt="" />
          <S.ErrorMessage>We're having an issue fetching your listings. Please check back later.</S.ErrorMessage>
        </S.ErrorContainer>
      )}

      <span ref={trackingRef} />
    </S.Container>
  );
};

const { array, bool, string, number, func } = PropTypes;

HubCollection.propTypes = {
  title: string.isRequired,
  ariaLabelVerb: string.isRequired,
  listings: array.isRequired,
  displayCount: number.isRequired,
  showOffMarketToggle: bool,
  handleOffMarketToggle: func,
  ctaMessage: string.isRequired,
  ctaLink: string.isRequired,
  noListingsMessage: string.isRequired,
  isLoading: bool.isRequired,
  error: bool.isRequired,
  analyticsLabel: string.isRequired,
};

export default HubCollection;
